import { UnknownAction } from 'redux';

import globalLogger from '../logger';
import { MiddlewareNextFunction, ReduxState, ReduxStore } from '../types/reduxTypes';
import { DRAG_END, DRAG_START } from '../reducers/draggingConstants';
import { LINE_POINT_DRAG_START } from '../element/line/lineUiConstants';
import {
    ELEMENT_CREATE,
    ELEMENT_CREATION_SOURCES,
    ELEMENT_MOVE_MULTI,
    ELEMENT_MOVE_OPERATIONS,
    ELEMENT_UPDATE,
    ELEMENT_UPDATE_TYPE,
} from '../../common/elements/elementConstants';
import {
    ElementCreateAction,
    isActionElementMoveMulti,
    isActionElementUpdate,
} from '../../common/elements/elementActionTypes';
import {
    ELEMENTS_DESELECT_ALL,
    ELEMENTS_DESELECTED,
    ELEMENTS_SELECTED,
} from '../../common/elements/selectionConstants';
import { CommentActionType } from '../../common/comments/commentConstants';
import { POPUP_OPEN, POPUP_TOGGLE } from '../components/popupPanel/popupConstants';
import { PopupToggleAction } from '../components/popupPanel/popupActionTypes';
import { BATCH_ACTION_TYPE } from '../../common/store/reduxBulkingMiddleware';
import { BoardSections } from '../../common/boards/boardConstants';
import { ElementType } from '../../common/elements/elementTypes';
import { getElements } from '../element/selectors/elementsSelector';
import { getUserIdFromAction } from '../../common/actionUtils';
import { ActionObject } from '../../common/actions/actionTypes';
import { MNElementLocation } from '../../common/elements/elementModelTypes';
import { ImmutableMap } from '../../common/utils/immutableHelper';
import { getRemoteSelectedElementDetails } from '../remoteActivity/remoteSelectedElementsSelector';
import { activePopupSelector } from '../components/popupPanel/popupOpenSelector';
import { getClosestAncestorBoardId, getElement } from '../../common/elements/utils/elementTraversalUtils';
import { isBoardLike, isTask } from '../../common/elements/utils/elementTypeUtils';
import { isLocationAttached } from '../../common/elements/utils/elementLocationUtils';
import { getPhysicalId } from '../../common/elements/utils/elementPropertyUtils';
import { getCurrentBoardId } from '../reducers/currentBoardId/currentBoardIdSelector';
import { getCurrentUserQuickNotesRootBoardId } from '../user/currentUserSelector';
import { getIsSoundsEnabledForCurrentUser } from './soundEnabledSelector';
import { playSound } from './soundService';
import { SoundId } from './soundConstants';

const logger = globalLogger.createChannel('sounds');

/** Fields the socket / bulking layers can add to any action */
type ActionMeta = { remote?: boolean; batched?: boolean };

// Local shapes for actions whose creators don't export a type
type DragStartAction = UnknownAction & { ids?: string[]; elementType?: string };
type DragEndAction = UnknownAction & {
    dropState?: { droppedElementIds?: string[] };
    dropLocation?: MNElementLocation;
};
type LinePointDragStartAction = UnknownAction & { isControlPoint?: boolean };
type SelectionAction = UnknownAction & { ids?: string[] };
type CommentUpdateAction = UnknownAction & { contentUpdate?: { updateType?: string } };
type BatchAction = UnknownAction & { payload?: UnknownAction[] };

type SoundRuleContext = {
    action: UnknownAction & ActionMeta;
    /** Store state captured before the action was reduced */
    prevState: ReduxState;
};

type SoundRule = (context: SoundRuleContext) => SoundId | SoundId[] | undefined;

const isRemote = (action: UnknownAction & ActionMeta) => !!action.remote;

const getElementsById = (state: ReduxState, ids: string[] | undefined) => {
    if (!ids?.length) return [];
    const elements = getElements(state);
    return ids.map((id) => getElement(elements, id)).filter(Boolean);
};

// ---------------------------------------------------------------------------
// Element pickup / drop
// ---------------------------------------------------------------------------

// Every element type shares the card pickup / drop sounds. Existing elements arrive as ids;
// toolbar creation-tool drags carry an `elementType` but no ids yet — either way, something is
// being dragged, so it sounds. Except attached comment pins: those only sound if the drag
// detaches them — the drag preview plays the pickup as the pin expands back into a card
// (see CommentThreadDragPreview.jsx).
const dragStartRule: SoundRule = ({ action, prevState }) => {
    const { ids, elementType } = action as DragStartAction;
    if (!ids?.length) return elementType ? SoundId.CardPickup : undefined;

    const draggedElements = getElementsById(prevState, ids);
    if (draggedElements.length && draggedElements.every(isLocationAttached)) return undefined;

    return SoundId.CardPickup;
};

// Grabbing a line endpoint counts as a pickup too; dragging the curve's control point stays silent.
// (The matching drop is component-driven — see ConnectedEdgeDragHandle.jsx — because only the drop
// target knows whether the edge connected to an element.)
const linePointDragStartRule: SoundRule = ({ action }) =>
    (action as LinePointDragStartAction).isControlPoint ? undefined : SoundId.CardPickup;

/**
 * Boards whose contents are on screen: the open board (its canvas and unsorted panel — resolved
 * to its physical id in case it's viewed through an alias) and quick notes. Drops into these
 * are regular drops, not drops "into a board".
 */
const getOnScreenContainerBoardIds = (prevState: ReduxState): (string | undefined)[] => {
    const currentBoardId = getCurrentBoardId(prevState);
    const currentBoardPhysicalId = getPhysicalId(getElement(getElements(prevState), currentBoardId));
    return [currentBoardId, currentBoardPhysicalId, getCurrentUserQuickNotesRootBoardId(prevState)];
};

// Cancelled drags dispatch dragEnd() with no dropState — only successful drops make a sound.
// Trash drops stay quiet here (the resulting move plays the delete sound), and drops into a
// board that isn't on screen (a board icon, a breadcrumb) get the board drop sound
const dragEndRule: SoundRule = ({ action, prevState }) => {
    const { dropState, dropLocation } = action as DragEndAction;
    if (!dropState?.droppedElementIds?.length) return undefined;

    if (dropLocation?.section === BoardSections.TRASH) return undefined;

    // A comment thread attaching to an element connects to it, rather than dropping onto it
    if (dropLocation?.section === BoardSections.ATTACHED) return SoundId.LineConnect;

    const dropParent = getElement(getElements(prevState), dropLocation?.parentId);
    const isDropIntoBoard =
        isBoardLike(dropParent) && !getOnScreenContainerBoardIds(prevState).includes(dropLocation?.parentId);
    return isDropIntoBoard ? SoundId.BoardDrop : SoundId.CardDrop;
};

// ---------------------------------------------------------------------------
// Element updates: legacy task completion, reactions
// ---------------------------------------------------------------------------

const elementUpdateRule: SoundRule = ({ action }) => {
    if (!isActionElementUpdate(action) || isRemote(action)) return undefined;

    if (action.updateType === ELEMENT_UPDATE_TYPE.REACTION) return SoundId.ReactionAdd;

    // Legacy (element-per-task) task lists
    if (action.updateType === ELEMENT_UPDATE_TYPE.TOGGLE_COMPLETION) {
        const update = action.updates[0];
        if (!update) return undefined;
        return update.changes?.isComplete ? SoundId.CheckboxTick : SoundId.CheckboxUntick;
    }

    return undefined;
};

/**
 * Remember what was just created, so e.g. the first task that comes with a brand-new task list
 * doesn't sound like a task being added.
 */
const RECENTLY_CREATED_WINDOW_MS = 1000;
const recentlyCreatedElementIds = new Map<string, number>();

const rememberCreatedElement = (id: string) => {
    const now = Date.now();
    recentlyCreatedElementIds.set(id, now);
    recentlyCreatedElementIds.forEach((createdAt, createdId) => {
        if (now - createdAt > RECENTLY_CREATED_WINDOW_MS) recentlyCreatedElementIds.delete(createdId);
    });
};

const wasRecentlyCreated = (id: string | undefined) => {
    if (!id) return false;
    const createdAt = recentlyCreatedElementIds.get(id);
    return createdAt !== undefined && Date.now() - createdAt <= RECENTLY_CREATED_WINDOW_MS;
};

/** Creation sources where the new element appears in place on the board, which sounds like a drop */
const DROP_SOUND_CREATION_SOURCES = new Set<string>([
    ELEMENT_CREATION_SOURCES.DOUBLE_CLICK,
    ELEMENT_CREATION_SOURCES.CONTEXT_MENU,
    ELEMENT_CREATION_SOURCES.PASTE,
    ELEMENT_CREATION_SOURCES.TASK_DRAG_OUT,
]);

const elementCreateRule: SoundRule = ({ action }) => {
    if (isRemote(action)) return undefined;

    const createAction = action as ElementCreateAction;
    if (createAction.id) rememberCreatedElement(createAction.id);

    // Duplicated elements landing on the board — option-drag, duplicate shortcuts, pasting copied
    // elements — sound like a drop (batches of creates de-dupe into a single sound below)
    if (createAction.duplicate) return SoundId.CardDrop;

    // Legacy task lists: the first task that comes with a freshly created list is part of creating
    // the list (which has already sounded), not a task being added — stay quiet
    if (createAction.elementType === ElementType.TASK_TYPE && wasRecentlyCreated(createAction.location?.parentId)) {
        return undefined;
    }

    // Creating an element in place — double-click, context menu, pasting external content,
    // dragging a task out of its list — sounds like a drop
    if (createAction.creationSource && DROP_SOUND_CREATION_SOURCES.has(createAction.creationSource)) {
        return SoundId.CardDrop;
    }

    // Legacy task lists: a new task element appearing
    return createAction.elementType === ElementType.TASK_TYPE ? SoundId.TaskNewTaskAppear : undefined;
};

// ---------------------------------------------------------------------------
// Delete (move to trash)
// ---------------------------------------------------------------------------

const elementMoveMultiRule: SoundRule = ({ action, prevState }) => {
    if (!isActionElementMoveMulti(action) || isRemote(action)) return undefined;

    const isTrash =
        action.moveOperation === ELEMENT_MOVE_OPERATIONS.TRASH ||
        action.moves.some((move) => move.location?.section === BoardSections.TRASH);
    if (!isTrash) return undefined;

    // Deleting individual task items (legacy task lists) isn't "deleting an element" — stay quiet
    // unless at least one whole (non-task) element is going to the trash
    const movedIds = action.moves.map((move) => move.id);
    const movedElements = getElementsById(prevState, movedIds);
    if (movedElements.length && movedElements.every(isTask)) return undefined;

    return SoundId.DeleteSomething;
};

// ---------------------------------------------------------------------------
// Selection (remote users only — local select / deselect are silent)
// ---------------------------------------------------------------------------

/**
 * Whether the given element is rendered on screen — i.e. its containing board is the open board
 * (or quick notes). Remote selection actions arrive for any board we have access to, but only
 * selections the user can actually see should sound.
 */
const isElementOnScreen = (prevState: ReduxState, elementId: string): boolean => {
    const elements = getElements(prevState);
    const ancestorBoardId = getClosestAncestorBoardId(elements, elementId);
    return !!ancestorBoardId && getOnScreenContainerBoardIds(prevState).includes(ancestorBoardId);
};

const elementsSelectedRule: SoundRule = ({ action, prevState }) => {
    if (!isRemote(action)) return undefined;
    const { ids } = action as SelectionAction;
    if (!ids?.length) return undefined;
    return ids.some((id) => isElementOnScreen(prevState, id)) ? SoundId.RemoteUserSelect : undefined;
};

/** Whether any of the given elements were visibly selected by the acting remote user before this action. */
const hadVisibleRemoteSelection = (prevState: ReduxState, ids: string[], userId: string | undefined): boolean => {
    const remoteSelectedElements = getRemoteSelectedElementDetails(prevState);
    return ids.some((id) => {
        // Deselected entries linger as {userId: null} (see selectedElementReducer), so check the
        // userId rather than entry existence
        const selectedByUserId = remoteSelectedElements?.getIn([id, 'userId']);
        if (!selectedByUserId || (userId && selectedByUserId !== userId)) return false;
        return isElementOnScreen(prevState, id);
    });
};

/** Whether the given remote user had any elements visibly selected before this action. */
const userHadVisibleRemoteSelection = (prevState: ReduxState, userId: string | undefined): boolean => {
    if (!userId) return false;
    const remoteSelectedElements = getRemoteSelectedElementDetails(prevState);
    return !!remoteSelectedElements?.some(
        (entry: ImmutableMap, elementId: string) =>
            entry?.get('userId') === userId && isElementOnScreen(prevState, elementId),
    );
};

// Deselect sounds only play for selections we could actually see (tracked in remoteActivity) —
// remote selection actions can arrive for boards / selections we have no visibility of
const elementsDeselectedRule: SoundRule = ({ action, prevState }) => {
    if (!isRemote(action)) return undefined;
    const { ids } = action as SelectionAction;
    if (!ids?.length) return undefined;
    const userId = getUserIdFromAction(action as ActionObject);
    return hadVisibleRemoteSelection(prevState, ids, userId) ? SoundId.RemoteUserDeselect : undefined;
};

// The client's deselectAll carries ids, but the server broadcasts an id-less deselect-all on every
// socket disconnect — whether or not the user had a selection — so those must stay silent unless
// the user visibly had something selected
const elementsDeselectAllRule: SoundRule = ({ action, prevState }) => {
    if (!isRemote(action)) return undefined;
    const { ids } = action as SelectionAction;
    const userId = getUserIdFromAction(action as ActionObject);
    const hadSelection = ids?.length
        ? hadVisibleRemoteSelection(prevState, ids, userId)
        : userHadVisibleRemoteSelection(prevState, userId);
    return hadSelection ? SoundId.RemoteUserDeselect : undefined;
};

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

// A collaborator's comment arriving — the local user's own comments, replies and edits are silent
const commentsAddRule: SoundRule = ({ action }) => (isRemote(action) ? SoundId.CommentAdd : undefined);

const commentsUpdateRule: SoundRule = ({ action }) => {
    if (isRemote(action)) return undefined;
    const { contentUpdate } = action as CommentUpdateAction;
    return contentUpdate?.updateType === ELEMENT_UPDATE_TYPE.REACTION ? SoundId.ReactionAdd : undefined;
};

// ---------------------------------------------------------------------------
// Popups
// ---------------------------------------------------------------------------

const popupOpenRule: SoundRule = () => SoundId.PopupOpen;

// A toggle only sounds when it opens the popup (closing is silent)
const popupToggleRule: SoundRule = ({ action, prevState }) =>
    activePopupSelector(prevState)?.has((action as Partial<PopupToggleAction>).popupId) ? undefined : SoundId.PopupOpen;

// ---------------------------------------------------------------------------
// Rule table
// ---------------------------------------------------------------------------

const SOUND_RULES: Record<string, SoundRule> = {
    [DRAG_START]: dragStartRule,
    [DRAG_END]: dragEndRule,
    [LINE_POINT_DRAG_START]: linePointDragStartRule,
    [ELEMENT_UPDATE]: elementUpdateRule,
    [ELEMENT_CREATE]: elementCreateRule,
    [ELEMENT_MOVE_MULTI]: elementMoveMultiRule,
    [ELEMENTS_SELECTED]: elementsSelectedRule,
    [ELEMENTS_DESELECTED]: elementsDeselectedRule,
    [ELEMENTS_DESELECT_ALL]: elementsDeselectAllRule,
    [CommentActionType.COMMENTS_ADD]: commentsAddRule,
    [CommentActionType.COMMENTS_UPDATE]: commentsUpdateRule,
    [POPUP_OPEN]: popupOpenRule,
    [POPUP_TOGGLE]: popupToggleRule,
};

/** Collects the sounds an action (or each action inside a batch) should trigger. */
const getSoundsForAction = (context: SoundRuleContext): SoundId[] => {
    const { action } = context;

    if (action.type === BATCH_ACTION_TYPE) {
        const payload = (action as BatchAction).payload || [];
        return payload.flatMap((batchedAction) => getSoundsForAction({ ...context, action: batchedAction }));
    }

    const rule = SOUND_RULES[action.type];
    if (!rule) return [];

    try {
        const result = rule(context);
        if (!result) return [];
        return Array.isArray(result) ? result : [result];
    } catch (error) {
        logger.warn(`Sound rule failed for action: ${action.type}`, error);
        return [];
    }
};

// ---------------------------------------------------------------------------
// Playback
// ---------------------------------------------------------------------------

/**
 * A remote selection *switch* arrives as a deselect action immediately followed by a select
 * (two separate socket actions). Hold the deselect sound briefly so a following select can
 * swallow it — observers should hear one sound for "selection moved", not deselect + select.
 */
const REMOTE_DESELECT_SOUND_GRACE_MS = 150;
let pendingRemoteDeselectTimeout: ReturnType<typeof setTimeout> | undefined;

const cancelPendingRemoteDeselectSound = () => {
    clearTimeout(pendingRemoteDeselectTimeout);
    pendingRemoteDeselectTimeout = undefined;
};

const scheduleRemoteDeselectSound = () => {
    cancelPendingRemoteDeselectSound();
    pendingRemoteDeselectTimeout = setTimeout(() => {
        pendingRemoteDeselectTimeout = undefined;
        playSound(SoundId.RemoteUserDeselect);
    }, REMOTE_DESELECT_SOUND_GRACE_MS);
};

const playSounds = (soundIds: SoundId[]) => {
    // De-dupe so e.g. a batch of 20 remote selections plays once
    const sounds = new Set(soundIds);

    if (sounds.has(SoundId.RemoteUserSelect)) {
        cancelPendingRemoteDeselectSound();
        sounds.delete(SoundId.RemoteUserDeselect);
    }

    sounds.forEach((soundId) =>
        soundId === SoundId.RemoteUserDeselect ? scheduleRemoteDeselectSound() : playSound(soundId),
    );
};

/**
 * Plays UX sounds in response to redux actions. Central hook point for action-driven sounds
 * so individual components don't need to know about audio. Component-driven sounds
 * (hover, toggles, drawing…) use usePlaySound instead.
 */
export default (store: ReduxStore) => (next: MiddlewareNextFunction) => (action: UnknownAction) => {
    const isRelevant = action.type === BATCH_ACTION_TYPE || !!SOUND_RULES[action.type];
    if (!isRelevant) return next(action);

    const prevState = store.getState();
    const result = next(action);

    if (!getIsSoundsEnabledForCurrentUser(prevState)) return result;

    playSounds(getSoundsForAction({ action, prevState }));

    return result;
};
